import { grpc } from "@mirage/service-dbx-api";
import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import { ONE_MINUTE_IN_MILLIS } from "@mirage/shared/util/constants";
import { register, unregister } from "@mirage/shared/util/jobs";
import { lastValueFrom } from "rxjs";
import { shouldSyncQuerySuggestions } from "./utils/should-sync-query-suggestions";

import type { QuerySuggestionsCache } from "../cache/query-suggestions-cache";

const logger = tagged("service-query-suggestions/sync");

const SYNC_QUERY_SUGGESTIONS_JOB_NAME = "query-suggestions-sync";
export const SYNC_INTERVAL_MS = ONE_MINUTE_IN_MILLIS * 120;

const DEBUG = false;
DEBUG satisfies false;

export function start(cache: QuerySuggestionsCache) {
  register(SYNC_QUERY_SUGGESTIONS_JOB_NAME, SYNC_INTERVAL_MS, true, () =>
    sync(cache),
  );
}

export function cancel() {
  unregister(SYNC_QUERY_SUGGESTIONS_JOB_NAME);
}

const metrics = namespace("query-suggestions");

export async function sync(cache: QuerySuggestionsCache) {
  try {
    const lastQuerySuggestionsSyncMs =
      await cache.getLastQuerySuggestionsSyncMs();

    const expiredSuggestionsRemoved = await cache.cleanupExpired();
    if (expiredSuggestionsRemoved > 0) {
      logger.debug(
        `Removed ${expiredSuggestionsRemoved} expired query-suggestions`,
      );
    }

    if (
      shouldSyncQuerySuggestions(lastQuerySuggestionsSyncMs, SYNC_INTERVAL_MS)
    ) {
      const querySuggestionsResult = await lastValueFrom(
        grpc.genQuerySuggestions(
          undefined,
          [],
          undefined,
          "dash",
          undefined,
          undefined,
        ),
      );

      metrics.stats(
        "query-suggestions-sync/count",
        querySuggestionsResult.querySuggestions.length,
      );
      metrics.counter("query-suggestions-sync/status", 1, {
        status: "success",
      });

      if (DEBUG) {
        logger.debug(
          `genQuerySuggestions`,
          querySuggestionsResult.querySuggestions.slice(0, 3),
        );
      }

      const timestamp = querySuggestionsResult.timestamp
        ? new Date(querySuggestionsResult.timestamp).getTime()
        : Date.now();

      await cache.set(
        "global",
        querySuggestionsResult.querySuggestions,
        timestamp,
      );

      await cache.saveQuerySuggestionsSyncedMs();
    }
  } catch (e) {
    logger.error("query-suggestions-sync failed", e);
    metrics.counter("query-suggestions-sync/status", 1, {
      status: "error",
    });
    throw e;
  }
}
